<template>
    <Head title="Close Account | Settings" />
    <div class="px-4 md:px-8" data-page="settings">
        <!-- Page header -->
        <div class="sm:flex sm:justify-between sm:items-center mb-10">
            <!-- Left: Title -->
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                    Account Settings
                </h1>
            </div>
        </div>
        <!-- END PAGE HEADER -->

        <!-- TABLE -->
        <div
            class="lg:bg-white dark:bg-slate-800 dark:lg:bg-slate-800 lg:shadow-lg dark:lg:shadow-slate-900/50 lg:rounded-sm lg:border lg:border-gray-200 dark:lg:border-slate-700 mb-8"
        >
            <div class="flex flex-col lg:flex-row">
                <div
                    class="flex flex-nowrap p-0 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:border-none lg:shadow-none"
                >
                    <SettingsNav />
                </div>

                <div
                    class="p-5 lg:px-0 lg:py-0 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:mb-0 lg:border-r-0 lg:border-x-0 lg:border-y-0 lg:!border-l lg:!border-gray-200 dark:lg:!border-slate-700 lg:shadow-none"
                    style="grow: 1"
                >
                    <div
                        class="flex flex-nowrap flex-col max-w-3xl lg:px-10 lg:py-10"
                    >
                        <div class="mb-5">
                            <h2
                                class="text-xl lg:text-2xl text-gray-800 dark:text-gray-100 font-bold mb-5"
                            >
                                Close Account
                            </h2>

                            <section class="mb-8 pb-8">
                                <h3
                                    class="text-lg lg:text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1"
                                >
                                    Are you sure you want to close your account?
                                </h3>
                                <p class="text-sm mb-2 text-slate-700 dark:text-slate-300">
                                    We understand that you may have a change of
                                    heart and no longer wish to use My10x. We
                                    hate to see you go, but no hard feelings!
                                </p>
                                <p class="text-sm mb-10 text-slate-700 dark:text-slate-300">
                                    Use the below feature to permanently wipe
                                    your data from My10x. Poof. Before you click
                                    it though, know that this is a permanent
                                    removal and
                                    <em>NO data is recoverable</em>
                                    . You've been warned! To proceed, simply
                                    click below.
                                </p>

                                <!-- Subscription Warning -->
                                <div
                                    v-if="hasActiveSubscription"
                                    class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 text-amber-800 dark:text-amber-200 px-4 py-3 rounded mb-6"
                                >
                                    <p class="font-bold text-sm mb-1">Active 10xTravel+ Subscription</p>
                                    <p class="text-sm" v-if="isLegacySubscriber">
                                        You have a grandfathered 10xTravel+ plan at $199/year. This plan is no longer available.
                                        If you delete your account, your subscription will be cancelled and <strong>you will not be able to re-subscribe to this plan</strong>.
                                    </p>
                                    <p class="text-sm" v-else>
                                        Your 10xTravel+ subscription will not renew after the current billing period.
                                        You'll continue to have access until the end of your current period.
                                    </p>
                                </div>

                                <!-- Legacy Subscriber Double Confirmation -->
                                <div
                                    v-if="isLegacySubscriber && !legacyConfirmed"
                                    class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 text-red-700 dark:text-red-200 px-4 py-4 rounded mb-6"
                                >
                                    <p class="font-bold text-sm mb-2">Legacy Subscription Confirmation Required</p>
                                    <p class="text-sm mb-3">
                                        Your grandfathered pricing of $199/year cannot be restored once cancelled. Please confirm you understand this is irreversible.
                                    </p>
                                    <label class="flex items-center">
                                        <input
                                            type="checkbox"
                                            v-model="legacyConfirmed"
                                            class="form-checkbox h-4 w-4 text-red-600"
                                        />
                                        <span class="ml-2 text-sm">
                                            I understand that my legacy $199/year plan will be permanently cancelled and cannot be re-accessed.
                                        </span>
                                    </label>
                                </div>

                                <!-- General Error Display -->
                                <div
                                    v-if="
                                        form.errors.general ||
                                        verificationForm.errors.general ||
                                        sendAccountDeletionCodeForm.errors.general ||
                                        resendError
                                    "
                                    class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-6"
                                >
                                    {{
                                        form.errors.general ||
                                        verificationForm.errors.general ||
                                        sendAccountDeletionCodeForm.errors.general ||
                                        resendError
                                    }}
                                </div>

                                <!-- Closure reason (optional, shown for both flows) -->
                                <div class="mb-8" v-if="!accountDeletionCodeExists && !accountDeletionVerified">
                                    <label
                                        class="block text-sm font-bold mb-1 text-slate-800 dark:text-slate-100"
                                        for="closure_reason"
                                    >
                                        Before you go, would you mind sharing why? (optional)
                                    </label>
                                    <textarea
                                        id="closure_reason"
                                        v-model="closureReason"
                                        class="form-input w-full"
                                        rows="3"
                                        placeholder="Let us know what we could improve..."
                                        maxlength="1000"
                                    ></textarea>
                                </div>

                                <div v-if="isSsoUser">
                                    <div v-if="accountDeletionCodeExists">
                                        <p class="text-sm text-gray-600 dark:text-slate-300 mb-5">
                                            We sent a 6-digit code to <strong>{{ userData.email }}</strong>. It may take a moment to arrive — check your spam folder if you don't see it.
                                        </p>
                                        <form
                                            action="/user-settings/close/verify/check"
                                            method="post"
                                            @submit.prevent="
                                                verifyAccountDeletionCode
                                            "
                                        >
                                            <div class="w-full max-w-sm mb-5">
                                                <label
                                                    class="block text-sm font-bold mb-1 text-slate-800 dark:text-slate-100"
                                                    for="verification_code"
                                                >
                                                    Verification Code
                                                </label>
                                                <input
                                                    type="text"
                                                    id="verification_code"
                                                    name="verification_code"
                                                    class="form-input w-full"
                                                    :class="{
                                                        'border-red-500':
                                                            verificationForm
                                                                .errors
                                                                .verification_code,
                                                    }"
                                                    placeholder="Enter the 6-digit code"
                                                    maxlength="6"
                                                    required
                                                    v-model="
                                                        verificationForm.verification_code
                                                    "
                                                />
                                                <p
                                                    class="text-red-500 text-xs mt-1"
                                                    v-if="
                                                        verificationForm.errors
                                                            .verification_code
                                                    "
                                                >
                                                    {{
                                                        verificationForm.errors
                                                            .verification_code
                                                    }}
                                                </p>
                                            </div>
                                            <button
                                                type="submit"
                                                class="btn btn-orange"
                                                :disabled="isLegacySubscriber && !legacyConfirmed"
                                                :class="{ 'opacity-50 cursor-not-allowed': isLegacySubscriber && !legacyConfirmed }"
                                            >
                                                Verify and Close Account
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-default ml-4"
                                                :disabled="resendCooldownSeconds > 0"
                                                :class="{ 'opacity-50 cursor-not-allowed': resendCooldownSeconds > 0 }"
                                                @click="resendCode"
                                            >
                                                {{ resendCooldownSeconds > 0 ? `Resend in ${resendCooldownSeconds}s` : "Didn't get it? Resend" }}
                                            </button>
                                            <a
                                                href="/user-settings/close/cancel"
                                                class="btn btn-default ml-4"
                                            >
                                                Cancel account deletion
                                            </a>
                                        </form>
                                    </div>
                                    <div v-else>
                                        <form
                                            action="/user-settings/close/verify/send"
                                            method="post"
                                            @submit.prevent="
                                                sendAccountDeletionCode
                                            "
                                        >
                                            <div class="w-full max-w-sm mb-8">
                                                <label
                                                    class="block text-sm font-bold mb-1 text-slate-800 dark:text-slate-100"
                                                    for="name"
                                                >
                                                    Type Your Full Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="signature"
                                                    class="form-input w-full"
                                                    required
                                                    v-model="
                                                        sendAccountDeletionCodeForm.signature
                                                    "
                                                />
                                                <p
                                                    class="text-red-500 text-xs mt-1"
                                                    v-if="
                                                        sendAccountDeletionCodeForm
                                                            .errors.signature
                                                    "
                                                >
                                                    {{
                                                        sendAccountDeletionCodeForm
                                                            .errors.signature
                                                    }}
                                                </p>
                                            </div>
                                            <button
                                                type="submit"
                                                class="btn btn-orange"
                                                :disabled="isLegacySubscriber && !legacyConfirmed"
                                                :class="{ 'opacity-50 cursor-not-allowed': isLegacySubscriber && !legacyConfirmed }"
                                            >
                                                Send Verification Code
                                            </button>
                                        </form>
                                    </div>
                                </div>

                                <div v-else>
                                    <form
                                        @submit.prevent="closeAccount"
                                        class="mt-8"
                                    >
                                        <div class="mb-4">
                                            <label
                                                class="block text-sm font-bold mb-1 text-slate-800 dark:text-slate-100"
                                                for="signature"
                                            >
                                                Please type your name to confirm
                                            </label>
                                            <input
                                                id="signature"
                                                v-model="form.signature"
                                                class="form-input w-full"
                                                placeholder="Your Name"
                                                required
                                            />
                                            <div
                                                v-if="form.errors.signature"
                                                class="text-red-500 text-sm mt-1"
                                            >
                                                {{ form.errors.signature }}
                                            </div>
                                        </div>

                                        <div class="mb-4">
                                            <label
                                                class="block text-sm font-bold mb-1 text-slate-800 dark:text-slate-100"
                                                for="password"
                                            >
                                                Enter your password
                                            </label>
                                            <input
                                                id="password"
                                                v-model="form.password"
                                                type="password"
                                                class="form-input w-full"
                                                placeholder="Your Password"
                                                required
                                            />
                                            <div
                                                v-if="form.errors.password"
                                                class="text-red-500 text-sm mt-1"
                                            >
                                                {{ form.errors.password }}
                                            </div>
                                        </div>

                                        <div
                                            class="flex items-center justify-between mt-8"
                                        >
                                            <Link
                                                href="/settings"
                                                class="btn btn-secondary"
                                            >
                                                Cancel
                                            </Link>
                                            <button
                                                type="submit"
                                                class="btn bg-red-600 hover:bg-red-700 text-white"
                                                :disabled="isLegacySubscriber && !legacyConfirmed"
                                                :class="{ 'opacity-50 cursor-not-allowed': isLegacySubscriber && !legacyConfirmed }"
                                            >
                                                Close Account
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Layout from '@/Layouts/Main.vue';
import { Head, Link, useForm, usePage, router } from '@inertiajs/vue3';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import SettingsNav from '@/Pages/Settings/partials/nav.vue';

const userData = computed(() => usePage().props.userData ?? {});

const props = defineProps({
    isSsoUser: Boolean,
    accountDeletionCodeExists: Boolean,
    accountDeletionVerified: {
        type: Boolean,
        default: false,
    },
    hasActiveSubscription: {
        type: Boolean,
        default: false,
    },
    isLegacySubscriber: {
        type: Boolean,
        default: false,
    },
    onGracePeriod: {
        type: Boolean,
        default: false,
    },
});

const verificationForm = useForm({
    verification_code: '',
});

const sendAccountDeletionCodeForm = useForm({
    signature: '',
    closure_reason: '',
    confirm_legacy_cancellation: false,
});

const finalizeAccountDeletion = () => {
    const data = {};
    if (props.isLegacySubscriber) {
        data.confirm_legacy_cancellation = legacyConfirmed.value;
    }
    router.post('/user-settings/close/finalize', data);
};

watch(
    () => props.accountDeletionVerified,
    () => {
        if (props.accountDeletionVerified) {
            finalizeAccountDeletion();
        }
    }
);

const closureReason = computed({
    get: () => props.isSsoUser ? sendAccountDeletionCodeForm.closure_reason : form.closure_reason,
    set: (val) => {
        if (props.isSsoUser) {
            sendAccountDeletionCodeForm.closure_reason = val;
        } else {
            form.closure_reason = val;
        }
    },
});

const legacyConfirmed = ref(false);

const form = useForm({
    signature: '',
    password: '',
    closure_reason: '',
    confirm_legacy_cancellation: false,
});

const closeAccount = () => {
    if (props.isLegacySubscriber) {
        form.confirm_legacy_cancellation = legacyConfirmed.value;
    }
    form.post('/user-settings/close');
};


const RESEND_COOLDOWN_SECONDS = 60;
const codeSentAt = ref(null);
const now = ref(Date.now());
const resendError = ref('');
let countdownInterval = null;

const resendCooldownSeconds = computed(() => {
    if (!codeSentAt.value) return 0;
    const elapsed = Math.floor((now.value - codeSentAt.value) / 1000);
    return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
});

const verifyAccountDeletionCode = () => {
    resendError.value = '';
    verificationForm.post('/user-settings/close/verify/check');
};

const sendAccountDeletionCode = () => {
    if (props.isLegacySubscriber) {
        sendAccountDeletionCodeForm.confirm_legacy_cancellation = legacyConfirmed.value;
    }
    sendAccountDeletionCodeForm.post('/user-settings/close/verify/send', {
        onSuccess: () => { codeSentAt.value = Date.now(); },
    });
};

const resendCode = () => {
    resendError.value = '';
    // Signature is stored in session; empty body lets the controller fall back to it.
    router.post('/user-settings/close/verify/send', {}, {
        onSuccess: () => { codeSentAt.value = Date.now(); },
        onError: (errors) => {
            resendError.value = errors.general || errors.signature || 'Failed to resend. Please try again.';
        },
    });
};

defineOptions({
    layout: Layout,
});

onMounted(() => {
    countdownInterval = setInterval(() => { now.value = Date.now(); }, 1000);
    if (props.accountDeletionVerified) {
        finalizeAccountDeletion();
    }
});

onUnmounted(() => {
    clearInterval(countdownInterval);
});
</script>
